import { useEffect, useState, type FormEvent } from 'react';
import { LayoutTemplate, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import {
  useCreateAudience, useUpdateAudience, type SaveAudienceInput,
} from '@/hooks/useNewsletter';
import type { Audience, AudienceRule } from '@/types/newsletter';

type Props = {
  open: boolean;
  onClose: () => void;
  audience?: Audience | null;
};

const EMPTY: SaveAudienceInput = {
  name: '',
  description: '',
  kind: 'static',
  rules: [],
  color: '#85572e',
};

const FIELD_OPTIONS: Array<{ value: AudienceRule['field']; label: string }> = [
  { value: 'status',      label: 'Status' },
  { value: 'tag',         label: 'Tag' },
  { value: 'locale',      label: 'Language' },
  { value: 'clientType',  label: 'Client Type' },
  { value: 'lastOpened',  label: 'Last opened a campaign' },
  { value: 'lastClicked', label: 'Last clicked a link' },
  { value: 'engagement',  label: 'Engagement score' },
];

const OP_OPTIONS: Array<{ value: AudienceRule['op']; label: string }> = [
  { value: 'equals',     label: 'is' },
  { value: 'not_equals', label: 'is not' },
  { value: 'contains',   label: 'contains' },
  { value: 'in',         label: 'is one of' },
  { value: 'before',     label: 'before' },
  { value: 'after',      label: 'after' },
  { value: 'at_least',   label: 'at least' },
  { value: 'at_most',    label: 'at most' },
];

export function AudienceFormModal({ open, onClose, audience }: Props) {
  const create = useCreateAudience();
  const update = useUpdateAudience();
  const editing = !!audience;

  const [form, setForm] = useState<SaveAudienceInput>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(audience ? {
        name: audience.name,
        description: audience.description ?? '',
        kind: audience.kind,
        rules: audience.rules ?? [],
        color: audience.color ?? '#85572e',
      } : EMPTY);
      setError(null);
    }
  }, [open, audience]);

  function setField<K extends keyof SaveAudienceInput>(key: K, val: SaveAudienceInput[K]) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  function addRule() {
    setField('rules', [...(form.rules ?? []), { field: 'status', op: 'equals', value: 'active' }]);
  }

  function updateRule(idx: number, patch: Partial<AudienceRule>) {
    const next = [...(form.rules ?? [])];
    next[idx] = { ...next[idx], ...patch };
    setField('rules', next);
  }

  function removeRule(idx: number) {
    setField('rules', (form.rules ?? []).filter((_, i) => i !== idx));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) { setError('Name is required.'); return; }
    if (form.kind === 'dynamic' && (!form.rules || form.rules.length === 0)) {
      setError('Dynamic audiences need at least one rule.');
      return;
    }
    try {
      if (editing && audience) {
        await update.mutateAsync({ id: audience.id, ...form });
        toast.success(`Updated ${form.name}`);
      } else {
        await create.mutateAsync(form);
        toast.success(`Created ${form.name}`);
      }
      onClose();
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Save failed.');
    }
  }

  const submitting = create.isPending || update.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      kicker={editing ? 'Edit Audience' : 'New Audience'}
      title={editing ? form.name : 'Create an audience.'}
      description="Static audiences are manually curated lists. Dynamic audiences auto-update based on subscriber attributes."
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button type="submit" form="aud-form" loading={submitting} leftIcon={<LayoutTemplate size={13} />}>
            {editing ? 'Save Changes' : 'Create Audience'}
          </Button>
        </>
      }
    >
      <form id="aud-form" onSubmit={onSubmit} className="space-y-5 py-2">
        <div className="grid grid-cols-2 gap-4">
          <Input label="Name" required placeholder="Active customers"
            value={form.name} onChange={(e) => setField('name', e.target.value)} />
          <Input label="Color" type="color"
            value={form.color ?? '#85572e'} onChange={(e) => setField('color', e.target.value)} />
        </div>

        <Textarea label="Description" rows={2}
          value={form.description ?? ''} onChange={(e) => setField('description', e.target.value)} />

        {/* Kind selector */}
        <div>
          <div className="kicker mb-2">Type</div>
          <div className="grid grid-cols-2 gap-2">
            <KindOption
              active={form.kind === 'static'}
              title="Static List"
              description="Manually curated — add/remove subscribers explicitly"
              onClick={() => setField('kind', 'static')}
            />
            <KindOption
              active={form.kind === 'dynamic'}
              title="Dynamic Segment"
              description="Auto-evaluated by rules — membership updates as data changes"
              onClick={() => setField('kind', 'dynamic')}
            />
          </div>
        </div>

        {form.kind === 'dynamic' && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="kicker mb-1">Rules</div>
                <p className="text-2xs text-text-dim">All rules must match (AND logic).</p>
              </div>
              <Button size="sm" variant="secondary" leftIcon={<Plus size={12} />} type="button" onClick={addRule}>
                Add Rule
              </Button>
            </div>

            {(form.rules ?? []).length === 0 ? (
              <div className="p-4 border border-dashed border-border rounded-sm text-center text-xs text-text-dim">
                No rules yet. Click "Add Rule" to define one.
              </div>
            ) : (
              <div className="space-y-2">
                {form.rules!.map((rule, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-bg-subtle border border-border rounded-sm p-2">
                    <span className="text-2xs kicker w-6 text-center tabular">{String(idx + 1).padStart(2, '0')}</span>
                    <div className="flex-1 grid grid-cols-3 gap-2">
                      <Select
                        value={rule.field}
                        onChange={(e) => updateRule(idx, { field: e.target.value as AudienceRule['field'] })}
                        options={FIELD_OPTIONS}
                      />
                      <Select
                        value={rule.op}
                        onChange={(e) => updateRule(idx, { op: e.target.value as AudienceRule['op'] })}
                        options={OP_OPTIONS}
                      />
                      <Input
                        placeholder="Value"
                        value={String(rule.value ?? '')}
                        onChange={(e) => updateRule(idx, { value: e.target.value })}
                      />
                    </div>
                    <button type="button" onClick={() => removeRule(idx)}
                      className="p-1.5 text-text-dim hover:text-danger hover:bg-danger/10 rounded-sm">
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {error && <div className="text-xs text-danger border-l-2 border-danger pl-3 py-1">{error}</div>}
      </form>
    </Modal>
  );
}

function KindOption({ active, title, description, onClick }: {
  active: boolean; title: string; description: string; onClick: () => void;
}) {
  return (
    <button type="button" onClick={onClick}
      className={`
        text-left p-3 border rounded-sm transition-colors
        ${active
          ? 'border-brass-400 bg-brass-400/5'
          : 'border-border hover:border-border-strong hover:bg-bg-hover'}
      `}
    >
      <div className="text-sm text-text font-medium">{title}</div>
      <div className="text-2xs text-text-dim mt-1 leading-relaxed">{description}</div>
    </button>
  );
}
